import React, { useEffect, useState } from 'react';
import { UserProfile } from '../types';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';

interface GlobalAgendaNotificationProps {
  userProfile: UserProfile | null;
}

interface AgendaReminderEvent {
  id: string;
  title: string;
  startTime: Date;
  reminderAt: Date | null;
  category?: string;
  reminderMinutesBefore?: number;
}

const GlobalAgendaNotification: React.FC<GlobalAgendaNotificationProps> = ({ userProfile }) => {
  const [nextEvent, setNextEvent] = useState<AgendaReminderEvent | null>(null);

  useEffect(() => {
    if (!userProfile?.uid) return;

    const eventsQuery = query(
      collection(db, 'calendarEvents'),
      where('participants', 'array-contains', userProfile.uid),
      orderBy('startTime', 'asc')
    );

    const unsubscribe = onSnapshot(eventsQuery, snapshot => {
      const now = Date.now();
      const upcoming: AgendaReminderEvent[] = [];

      snapshot.forEach(docSnap => {
        const data: any = docSnap.data();
        if (!data.reminderEnabled || data.reminderSent) return;

        const startTime: Date = data.startTime?.toDate ? data.startTime.toDate() : new Date();
        const reminderAt: Date | null = data.reminderAt?.toDate
          ? data.reminderAt.toDate()
          : null;

        if (!reminderAt) return;

        // Alleen events in de toekomst met reminder die nu of al geweest is
        if (startTime.getTime() < now) return;
        if (reminderAt.getTime() > now) return;

        upcoming.push({
          id: docSnap.id,
          title: data.title || 'Afspraak',
          startTime,
          reminderAt,
          category: data.category,
          reminderMinutesBefore: data.reminderMinutesBefore
        });
      });

      if (upcoming.length === 0) {
        setNextEvent(null);
        return;
      }

      // Kies de dichtstbijzijnde afspraak op starttijd
      upcoming.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      setNextEvent(upcoming[0]);
    });

    return () => unsubscribe();
  }, [userProfile]);

  if (!userProfile || !nextEvent) {
    return null;
  }

  const start = nextEvent.startTime;

  const formattedTime = start.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const formattedDate = start.toLocaleDateString('nl-NL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });

  const handleDismiss = async () => {
    setNextEvent(null);
    try {
      await updateDoc(doc(db, 'calendarEvents', nextEvent.id), {
        reminderSent: true
      });
    } catch (e) {
      console.error('Error marking reminder as sent:', e);
    }
  };

  const minutesText =
    nextEvent.reminderMinutesBefore && nextEvent.reminderMinutesBefore >= 5
      ? nextEvent.reminderMinutesBefore >= 60
        ? `${nextEvent.reminderMinutesBefore / 60} uur`
        : `${nextEvent.reminderMinutesBefore} min`
      : 'zo meteen';

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm w-full px-3 sm:px-0">
      <div className="shadow-2xl rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #7A9488 0%, #8DA8A0 50%, #A5BDB5 100%)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)' }}>
          <div className="flex items-center space-x-2">
            <span className="text-lg">⏰</span>
            <div className="text-sm font-semibold">
              Afspraak over {minutesText}
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white text-xs px-2 py-1 rounded-md hover:bg-white/10"
          >
            Sluiten
          </button>
        </div>
        <div className="px-4 py-3 text-sm space-y-1" style={{ color: 'rgba(255,255,255,0.95)' }}>
          <div className="font-medium break-words text-white">{nextEvent.title}</div>
          <div style={{ color: 'rgba(255,255,255,0.80)' }}>
            {formattedDate} • {formattedTime}
          </div>
          {nextEvent.category && (
            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.70)' }}>
              Categorie:{' '}
              <span className="font-medium text-white">
                {nextEvent.category === 'work'
                  ? 'Werk'
                  : nextEvent.category === 'friends'
                  ? 'Vrienden'
                  : nextEvent.category === 'family'
                  ? 'Familie'
                  : nextEvent.category === 'activity'
                  ? 'Activiteit'
                  : nextEvent.category === 'action'
                  ? 'Actie'
                  : nextEvent.category}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalAgendaNotification;



